/** @typedef {import('../types/anomalyTimeSeries.types').TimeSeriesDataPoint} TimeSeriesDataPoint */
/** @typedef {import('../types/anomalyTimeSeries.types').PatternCard} PatternCard */

import { SEVERITY_RANK } from "./timelineHealth";

const DEVIATION_THRESHOLD = 15;

/**
 * @param {TimeSeriesDataPoint[]} dataPoints
 * @returns {number[][]} arrays of consecutive indices with hasAnomaly
 */
const findAnomalyRuns = (dataPoints) => {
  const runs = [];
  let current = [];
  dataPoints.forEach((dp, i) => {
    if (dp.hasAnomaly) {
      current.push(i);
    } else if (current.length) {
      runs.push(current);
      current = [];
    }
  });
  if (current.length) runs.push(current);
  return runs;
};

/**
 * @param {TimeSeriesDataPoint[]} dataPoints
 * @returns {PatternCard[]}
 */
export const detectPatterns = (dataPoints) => {
  /** @type {PatternCard[]} */
  const patterns = [];
  const anomalyPoints = dataPoints.filter((dp) => dp.hasAnomaly);
  if (!anomalyPoints.length) return patterns;

  const anomalyPeriods = anomalyPoints.map((dp) => dp.period);

  if (anomalyPoints.length === 1) {
    const dp = anomalyPoints[0];
    const pct = dp.maxDeviationPct;
    if (pct != null && pct > DEVIATION_THRESHOLD) {
      patterns.push({
        id: "one-time-spike",
        titleKey: "MetricAnomalyTimeline.patternSpike.title",
        explanationKey: "MetricAnomalyTimeline.patternSpike.explanation",
        actionKey: "MetricAnomalyTimeline.patternSpike.action",
        periods: [dp.period],
      });
    } else if (pct != null && pct < -DEVIATION_THRESHOLD) {
      patterns.push({
        id: "one-time-drop",
        titleKey: "MetricAnomalyTimeline.patternDrop.title",
        explanationKey: "MetricAnomalyTimeline.patternDrop.explanation",
        actionKey: "MetricAnomalyTimeline.patternDrop.action",
        periods: [dp.period],
      });
    }
  }

  const nonConsecutive = anomalyPoints.filter((dp, i, arr) => {
    if (i === 0) return true;
    const prevIdx = dataPoints.findIndex((p) => p.period === arr[i - 1].period);
    const curIdx = dataPoints.findIndex((p) => p.period === dp.period);
    return curIdx - prevIdx > 1;
  });
  if (anomalyPoints.length >= 2 && nonConsecutive.length >= 2) {
    patterns.push({
      id: "recurring",
      titleKey: "MetricAnomalyTimeline.patternRecurring.title",
      explanationKey: "MetricAnomalyTimeline.patternRecurring.explanation",
      actionKey: "MetricAnomalyTimeline.patternRecurring.action",
      periods: anomalyPeriods,
    });
  }

  const runs = findAnomalyRuns(dataPoints);
  const persistentRun = runs.find((run) => run.length >= 3);
  if (persistentRun) {
    patterns.push({
      id: "persistent",
      titleKey: "MetricAnomalyTimeline.patternPersistent.title",
      explanationKey: "MetricAnomalyTimeline.patternPersistent.explanation",
      actionKey: "MetricAnomalyTimeline.patternPersistent.action",
      periods: persistentRun.map((i) => dataPoints[i].period),
    });
  }

  const missingData = anomalyPoints.filter((dp) => dp.value == null);
  if (missingData.length) {
    patterns.push({
      id: "missing-data",
      titleKey: "MetricAnomalyTimeline.patternMissingData.title",
      explanationKey: "MetricAnomalyTimeline.patternMissingData.explanation",
      actionKey: "MetricAnomalyTimeline.patternMissingData.action",
      periods: missingData.map((dp) => dp.period),
    });
  }

  runs.forEach((run) => {
    const afterIdx = run[run.length - 1] + 1;
    if (afterIdx + 1 < dataPoints.length) {
      const cleanAfter = dataPoints.slice(afterIdx, afterIdx + 2).every((dp) => !dp.hasAnomaly);
      if (cleanAfter && run.length >= 1) {
        const existing = patterns.find((p) => p.id === "recovered");
        const periods = run.map((i) => dataPoints[i].period);
        if (!existing) {
          patterns.push({
            id: "recovered",
            titleKey: "MetricAnomalyTimeline.patternRecovered.title",
            explanationKey: "MetricAnomalyTimeline.patternRecovered.explanation",
            actionKey: "MetricAnomalyTimeline.patternRecovered.action",
            periods,
          });
        }
      }
    }
  });

  if (anomalyPoints.length >= 2) {
    const first = anomalyPoints[0];
    const last = anomalyPoints[anomalyPoints.length - 1];
    const sevWorse =
      (SEVERITY_RANK[last.maxSeverity] || 0) > (SEVERITY_RANK[first.maxSeverity] || 0);
    const devWorse =
      Math.abs(last.maxDeviationPct || 0) > Math.abs(first.maxDeviationPct || 0);
    if (sevWorse || devWorse) {
      patterns.push({
        id: "worsening",
        titleKey: "MetricAnomalyTimeline.patternWorsening.title",
        explanationKey: "MetricAnomalyTimeline.patternWorsening.explanation",
        actionKey: "MetricAnomalyTimeline.patternWorsening.action",
        periods: [first.period, last.period],
      });
    }
  }

  return patterns;
};
